import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const DISMISS_KEY = "installPromptDismissedAt";
const DISMISS_DAYS = 7;

const ua = () => window.navigator.userAgent || "";

const isIos = () =>
  /iphone|ipad|ipod/i.test(ua()) ||
  // iPadOS 13+ reports itself as a Mac
  (/macintosh/i.test(ua()) && window.navigator.maxTouchPoints > 1);
const isAndroid = () => /android/i.test(ua());
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

// Browsers embedded inside other apps (Facebook, Instagram, ...). They cannot add to home screen.
const IN_APP = /FBAN|FBAV|FB_IAB|Instagram|Line\/|Snapchat|TikTok|musical_ly|MicroMessenger|Messenger|Twitter/i;

// Which set of instructions matches this phone + browser.
// Returns one of the keys in install.variants (or "iosInApp"/"androidInApp", which reuse them).
const detectVariant = () => {
  const agent = ua();
  if (isIos()) {
    if (IN_APP.test(agent)) return "iosOther";
    if (/CriOS/i.test(agent)) return "iosChrome";
    if (/FxiOS|EdgiOS|OPiOS|OPT\//i.test(agent)) return "iosOther";
    if (/Safari/i.test(agent)) return "iosSafari";
    return "iosOther"; // WebView without the Safari token
  }
  if (isAndroid()) {
    if (IN_APP.test(agent) || /; wv\)/i.test(agent)) return "androidInApp";
    if (/SamsungBrowser/i.test(agent)) return "androidSamsung";
    if (/Firefox/i.test(agent)) return "androidFirefox";
    if (/EdgA/i.test(agent)) return "androidEdge";
    if (/MiuiBrowser|XiaoMi/i.test(agent)) return "androidMi";
    if (/OPR\/|Opera|UCBrowser|Vivaldi|DuckDuckGo|Brave/i.test(agent)) return "androidOther";
    if (/Chrome/i.test(agent)) return "androidChrome";
    return "androidOther";
  }
  return null;
};

// How each variant is shown: which steps list, and an optional "open in another browser" step first.
const VARIANTS = {
  iosSafari: { steps: "iosSafari" },
  iosChrome: { steps: "iosChrome" },
  iosOther: { steps: "iosSafari", pre: "openSafari" },
  androidChrome: { steps: "androidChrome" },
  androidSamsung: { steps: "androidSamsung" },
  androidFirefox: { steps: "androidFirefox" },
  androidEdge: { steps: "androidEdge" },
  androidMi: { steps: "androidMi" },
  androidOther: { steps: "androidOther" },
  androidInApp: { steps: "androidChrome", pre: "openChrome" },
};

const IOS_CHOICES = ["iosSafari", "iosChrome", "iosOther"];
const ANDROID_CHOICES = ["androidChrome", "androidSamsung", "androidFirefox", "androidEdge", "androidMi", "androidOther"];

const recentlyDismissed = () => {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return !!at && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
};

const InstallPrompt = () => {
  const { t } = useLang();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState(null); // steps to show (null = show Install button / subtitle only)
  const [showOthers, setShowOthers] = useState(false);
  const [copied, setCopied] = useState(false);

  const platform = isIos() ? "ios" : isAndroid() ? "android" : null;

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS has no beforeinstallprompt event, so show manual steps for the exact browser.
    if (isIos()) {
      setVariant(detectVariant());
      setVisible(true);
    }

    // On some Android setups (Samsung/Mi browsers, in-app browsers, older Chrome) the
    // install event never fires. Fall back to manual steps after a short wait.
    let fallbackTimer;
    if (isAndroid() && !isIos()) {
      fallbackTimer = setTimeout(() => {
        setDeferredPrompt((current) => {
          if (!current) {
            setVariant(detectVariant());
            setVisible(true);
          }
          return current;
        });
      }, 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    setVisible(false);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const handleCopy = async () => {
    if (await copyText(window.location.href)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // Can the native Install button be used right now?
  const canNativeInstall = !!deferredPrompt && !variant;

  const handleShowOthers = () => {
    setShowOthers((s) => !s);
  };

  const chooseVariant = (key) => {
    setVariant(key);
    setShowOthers(false);
  };

  if (!visible) return null;

  const config = variant ? VARIANTS[variant] : null;
  const steps = config ? t(`install.steps.${config.steps}`) : [];
  const choices = platform === "ios" ? IOS_CHOICES : platform === "android" ? ANDROID_CHOICES : [];
  const needsCopy = !!config?.pre;

  return (
    <div
      data-testid="install-prompt-banner"
      className="fixed bottom-16 md:bottom-4 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md max-h-[75vh] overflow-y-auto bg-white border border-[#E5E7EB] shadow-lg rounded-2xl px-4 py-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#123A2C] flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#14251F]">{t("install.title")}</p>
          <p className="text-xs text-[#6B7280]">{t("install.subtitle")}</p>
        </div>
        {canNativeInstall && (
          <button
            onClick={handleInstall}
            data-testid="install-prompt-install-btn"
            className="text-sm font-semibold text-white bg-[#123A2C] px-3 py-1.5 rounded-lg shrink-0"
          >
            {t("install.install")}
          </button>
        )}
        <button
          onClick={handleDismiss}
          data-testid="install-prompt-dismiss-btn"
          className="text-[#9CA3AF] shrink-0 self-start p-1 -mr-1"
          aria-label={t("install.dismiss")}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {config && (
        <div data-testid="install-prompt-steps" className="mt-3 border-t border-[#E5E7EB] pt-3">
          {needsCopy && (
            <div className="mb-3 rounded-lg bg-[#F6F7F4] p-2.5">
              <p className="text-xs text-[#14251F] leading-relaxed">{t(`install.${config.pre}`)}</p>
              <button
                onClick={handleCopy}
                data-testid="install-prompt-copy-btn"
                className="mt-2 text-xs font-semibold text-white bg-[#123A2C] px-3 py-1.5 rounded-lg"
              >
                {copied ? t("install.copied") : t("install.copyLink")}
              </button>
            </div>
          )}
          <p className="text-xs font-semibold text-[#14251F] mb-2">{t("install.followSteps")}</p>
          <ol className="space-y-2">
            {Array.isArray(steps) &&
              steps.map((text, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-[#123A2C] text-white text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-[#14251F] leading-snug">{text}</span>
                </li>
              ))}
          </ol>
          <p className="text-xs text-[#6B7280] mt-3">{t("install.done")}</p>
        </div>
      )}

      {choices.length > 0 && (
        <div className="mt-2">
          <button
            onClick={handleShowOthers}
            data-testid="install-prompt-other-btn"
            className="text-xs font-semibold text-[#123A2C] underline"
          >
            {t("install.notWorking")}
          </button>
          {showOthers && (
            <div className="mt-2">
              <p className="text-xs text-[#6B7280] mb-1.5">{t("install.chooseOther")}</p>
              <div className="flex flex-wrap gap-1.5">
                {choices.map((key) => (
                  <button
                    key={key}
                    onClick={() => chooseVariant(key)}
                    data-testid={`install-prompt-variant-${key}`}
                    className={`text-xs px-2.5 py-1.5 rounded-full border ${
                      variant === key
                        ? "bg-[#123A2C] text-white border-[#123A2C]"
                        : "bg-white text-[#14251F] border-[#E5E7EB]"
                    }`}
                  >
                    {t(`install.variants.${key}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InstallPrompt;
